// The app's routes (BUILD-PLAN stage 1, "Paths"):
// - Logged out, any path shows that path's login door; a path that isn't a product's shows the
//   Library's door.
// - Logging in, through any door, lands on the Library (decision 29). Once logged in, a link opens
//   the page it points to. Logging in from the property strategy report's link lands on the Library
//   too, with a line at the top and 1 button to open the report (Adam, 30 Oct 2026).
// - Until a product moves in, its path sends people to where it lives now (config/products.js).
// - /verify/<number>/<token>, a certificate's check link, opens logged out and logged in alike, ahead
//   of the login door: certificate-check is 1 of the 3 functions that answer without a login.
// - A product's own paths (/funding-engine/week-2?t=08m51s, /<product>/resources, ...) are
//   product/ProductRoute.jsx's.
// - /sign-in, the login link's page (screens/SignIn.jsx): logged out, its Log in button; logging in
//   there lands on the Library like any door; logged in already, it opens the Library and leaves the
//   link unused.
// - /free-webinars/replay/<id> is a replay's own page, where it plays (Adam, 23 Oct); any other address
//   under /free-webinars is Free webinars itself.
// - /register/<slug>?src=<source>, a free replay's register page (docs/replay-register-spec.md, Adam's
//   cut-down version of 23 Oct), opens logged out and logged in alike, ahead of the login door, as /verify
//   does: replay-register is the 4th function that answers without a login (decision 57). It logs nobody
//   in, so where a login lands doesn't change.
// - /property-strategy, Adam's free property strategy tool (docs/property-strategy-spec.md, Stage 1), opens
//   logged out and logged in alike, ahead of the login door, as /register does. It calls no function and reads
//   nothing. Staging only for now: on production (strategy/route.js) the path falls through as it always has.
import { Component, useEffect, useRef } from 'react'
import { CONFIGURED, ENV } from '../lib/env.js'
import { useAuth, logOut } from '../lib/auth.js'
import { navigate, usePath } from '../lib/router.jsx'
import { SITE } from '../config/site.js'
import { doorFor } from '../config/doors.js'
import { basePath, configRedirect } from '../config/products.js'
import { MemberProvider, useMember } from './member.jsx'
import Login from '../screens/Login.jsx'
import Library from '../screens/Library.jsx'
import AdminGate from '../admin/AdminGate.jsx'
import { ViewAsBar, stopViewingBeforeLogOut } from '../lib/viewAs.jsx'
import Account from '../screens/Account.jsx'
import FreeResources from '../screens/FreeResources.jsx'
import FreeWebinars from '../screens/FreeWebinars.jsx'
import WebinarReplay from '../screens/WebinarReplay.jsx'
import { replayIdIn } from '../free/webinars.js'
import Verify from '../screens/Verify.jsx'
import Register from '../screens/Register.jsx'
import PropertyStrategy from '../screens/PropertyStrategy.jsx'
import StrategyReport from '../screens/StrategyReport.jsx'
import { STRATEGY_PATH, strategyOpen, isStrategyReport } from '../strategy/route.js'
import { markReportLanding } from '../library/reportLanding.js'
import SignIn from '../screens/SignIn.jsx'
import { SIGN_IN_PATH } from '../lib/loginLink.js'
import ProductRoute from '../product/ProductRoute.jsx'

export default function App() {
  if (!CONFIGURED) {
    return (
      <Notice title="No Supabase settings">
        This build has no HUB_SUPABASE_URL or HUB_SUPABASE_PUBLISHABLE_KEY. Add them to a .env file for a local build; Netlify sets them per context.
      </Notice>
    )
  }
  return <Crashes><Root /></Crashes>
}

function Root() {
  const path = usePath()
  const auth = useAuth()
  const before = useRef(auth.status)
  // a fresh log in, from a door, rather than a session already open when the page loaded
  const justLoggedIn = before.current === 'out' && auth.status === 'in'
  // the login link's page: once logged in, by its button or before it opened, it's the Library
  const onSignIn = basePath(path) === SIGN_IN_PATH

  // a register page isn't a door: a login in another tab leaves the replay playing here
  const onRegister = basePath(path) === '/register'
  // nor is the property strategy tool: a login in another tab leaves the visitor mid-assessment. Its saved report,
  // /property-strategy/report, is a member page (Stage 2): logged out it's the Library's door, as any other path
  const onStrategy = strategyOpen(ENV.context) && basePath(path) === STRATEGY_PATH && !isStrategyReport(path)
  // a login from the report link still lands on the Library (decision 29), with the line to open the report on top
  // (Adam, 30 Oct 2026; library/reportLanding.js). Marked here, while rendering, so the Library sees it on its first
  // render: its effects run before this component's.
  if (justLoggedIn && strategyOpen(ENV.context) && isStrategyReport(path)) markReportLanding()

  useEffect(() => {
    if (!onRegister && !onStrategy && (justLoggedIn || (onSignIn && auth.status === 'in')) && path !== '/') navigate('/', { replace: true })
    before.current = auth.status
  })

  const redirect = configRedirect(path)
  useEffect(() => { if (redirect) navigate(redirect, { replace: true }) }, [redirect])

  // the certificate check, before the login: it needs no login and shows nothing else
  if (basePath(path) === '/verify') return <Verify path={path} />
  // a free replay's register page, before the login too: the same page whoever opens it
  if (onRegister) return <Register key={path} path={path} />
  // the property strategy tool, before the login too: the same page whoever opens it (staging only for now)
  if (onStrategy) return <PropertyStrategy auth={auth} />
  if (redirect || auth.status === 'loading') return <div className="boot" />
  if (auth.status === 'out') {
    if (onSignIn) return <SignIn />
    const door = doorFor(path)
    return <Login key={door.key} door={door} />
  }
  return (
    <MemberProvider session={auth.session}>
      <MemberRoutes path={justLoggedIn || onSignIn ? '/' : path} />
    </MemberProvider>
  )
}

function MemberRoutes({ path }) {
  const { me, session, loadMe } = useMember()
  if (me.status === 'loading') return <div className="boot" />
  if (me.status === 'error') {
    return (
      <Notice title="That didn't load">
        Check your connection and try again. If it keeps happening, email {SITE.helpEmail}.
        <span className="notice-actions"><button type="button" className="btn btn-ink" onClick={loadMe}>Try again</button></span>
      </Notice>
    )
  }
  if (me.status === 'none') return <NoPerson email={session.user.email} />

  // While staff view as a member, the bar naming them, with Stop viewing, sits on top of every page,
  // admin's included (lib/viewAs.jsx; stage 5)
  return (
    <>
      <ViewAsBar />
      <MemberPage path={path} />
    </>
  )
}

function MemberPage({ path }) {
  if (path === '/') return <Library />
  switch (basePath(path)) {
    case '/account': return <Account />
    case '/free-resources': return <FreeResources />
    case '/free-webinars': {
      const replay = replayIdIn(path)
      return replay ? <WebinarReplay key={replay} id={replay} /> : <FreeWebinars />
    }
    case '/admin': return <AdminGate path={path} />
    case STRATEGY_PATH: return strategyOpen(ENV.context) && isStrategyReport(path) ? <StrategyReport /> : <ProductRoute path={path} />
    default: return <ProductRoute path={path} />
  }
}

// Logged in, but no hub account behind the login: a password login on the shared project that
// has no people row, or someone whose access is all revoked. Nothing in the hub is theirs. The
// words suit both (Adam, 3 Oct).
function NoPerson({ email }) {
  const { me } = useMember()
  async function out() {
    await stopViewingBeforeLogOut(me)
    await logOut()
    navigate('/', { replace: true })
  }
  return (
    <Notice title="There's nothing here for this login">
      We can't find anything open to you for {email}. If you paid with a different email, log in with that one. If you think this is wrong, email {SITE.helpEmail}.
      <span className="notice-actions"><button type="button" className="btn btn-ink" onClick={out}>Log out</button></span>
    </Notice>
  )
}

function Notice({ title, children }) {
  return (
    <main className="notice">
      <p className="notice-mark" aria-hidden="true" />
      <h1>{title}</h1>
      <p>{children}</p>
    </main>
  )
}

class Crashes extends Component {
  constructor(props) { super(props); this.state = { failed: false } }
  static getDerivedStateFromError() { return { failed: true } }
  componentDidCatch(error) { console.error(error) }
  render() {
    if (!this.state.failed) return this.props.children
    return <Notice title="Something went wrong">Reload the page to try again. If it keeps happening, email {SITE.helpEmail}.</Notice>
  }
}
